/**
 * Vue directive: `v-horizontal-scroll`
 *
 * Converts vertical wheel events to horizontal scroll on the element.
 *
 * Register globally in main.ts:
 *   import { vHorizontalScroll } from "@/libraries/vertical-scroll-converter";
 *   app.directive("horizontal-scroll", vHorizontalScroll);
 *
 * Usage in templates:
 *   <ul v-horizontal-scroll />
 *   <ul v-horizontal-scroll="{ speedMultiplier: 1.5 }" />
 */

import type { Directive, DirectiveBinding } from 'vue';
import { applyHorizontalScroll, type ScrollConverterOptions, type ScrollConverterInstance } from './core';

// Store the instance on the element so we can destroy it on unmount
const KEY = Symbol('horizontalScroll');

type ElementWithInstance = HTMLElement & {
  [KEY]?: ScrollConverterInstance;
};

type ScrollConverterBinding = ScrollConverterOptions | false | null | undefined;

export const vHorizontalScroll: Directive<HTMLElement, ScrollConverterBinding> = {
  mounted(el: ElementWithInstance, binding: DirectiveBinding<ScrollConverterBinding>) {
    if (binding.value === false || binding.value === null) return;
    el[KEY] = applyHorizontalScroll(el, binding.value ?? {});
  },

  updated(el: ElementWithInstance, binding: DirectiveBinding<ScrollConverterBinding>) {
    // Re-attach if binding value changes (new options object)
    el[KEY]?.destroy();
    delete el[KEY];
    if (binding.value === false || binding.value === null) return;
    el[KEY] = applyHorizontalScroll(el, binding.value ?? {});
  },

  unmounted(el: ElementWithInstance) {
    el[KEY]?.destroy();
    delete el[KEY];
  },
};
